import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import Card from "@common/Card";
import DropDown from "@forms/DropDown";
import { useRef } from "react";
import { useNFTPurchase } from "@hooks/useNFTPurchase";
import constant from "@utils/constant";
import { getBuyFilters, getBuySort } from "@utils/common";
import { logError } from "@utils/log";
import Spinner from "@common/Spinner";
import Loader from "@common/Loader";
import Pagination from "@common/Pagination";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from "@context/AppContext";

const Buy = () => {
  const {
    themeConfig,
    siteDetails,
    walletAddress,
    connectWallet,
    isWalletConnected,
    refreshBuyData,
    buyNftData,
    buyLoading,
    setError
  } = useAppContext();
  const { tokenId } = useParams();
  const filterData = siteDetails?.filterData;
  const navigate = useNavigate();
  const loading = buyLoading;
  const mountedRef = useRef(true);
  const navigateRef = useRef(navigate);
  const { purchaseNFT, isPurchasing } = useNFTPurchase({
    siteDetails,
    connectWallet,
    isWalletConnected,
    navigate,
    mountedRef
  });
  // UI state
  const [priceHeader, setPriceHeader] = useState("Price");
  const [isListOpen, setList] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(undefined);
  const [style, setStyle] = useState();
  const [styleType, setStyleType] = useState();
  const [filterType, setFilterType] = useState();
  const [boxDisplay, setBoxDisplay] = useState(false);
  const [owner, setOwner] = useState("");
  const [isInputOpen, setInputOpen] = useState(false);
  // Data state
  const [nftData, setNftData] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [currentPage, setPage] = useState(1);
  const [spinner, setSpinner] = useState(false);

  // Memoize filters and sort options
  const filterOptions = useMemo(() => getBuyFilters(
    constant.priceFilter,
    filterData?.artistBackground,
    filterData?.pattern
  ), [filterData]);

  const sortOptions = useMemo(() => getBuySort(), []);
  // Load NFT data and apply initial filters
  useEffect(() => {
    setNftData([]);
    setFilteredCards([]);
    setPriceHeader("Price");
    setList(false);
    setBoxDisplay(false);
    setPage(1);
    try {
      let nft;
      if (tokenId) {
        nft = [
          ...buyNftData.filter(card => Number(card.tokenId) === Number(tokenId)),
          ...buyNftData.filter(card => Number(card.tokenId) !== Number(tokenId) && card.forSale && walletAddress !== card.currentOwner),
          ...buyNftData.filter(card => Number(card.tokenId) !== Number(tokenId) && !card.forSale && walletAddress !== card.currentOwner),
          ...buyNftData.filter(card => Number(card.tokenId) !== Number(tokenId) && card.forSale && walletAddress === card.currentOwner),
          ...buyNftData.filter(card => Number(card.tokenId) !== Number(tokenId) && !card.forSale && walletAddress === card.currentOwner),
        ];
      } else {
        nft = [
          ...buyNftData.filter(card => card.forSale && walletAddress !== card.currentOwner),
          ...buyNftData.filter(card => !card.forSale && walletAddress !== card.currentOwner),
          ...buyNftData.filter(card => card.forSale && walletAddress === card.currentOwner),
          ...buyNftData.filter(card => !card.forSale && walletAddress === card.currentOwner),
        ];
      }
      setNftData(nft);
      setFilteredCards(nft);
    } catch (err) {
      logError(err);
    }
    return () => {
      setNftData([]);
      setFilteredCards([]);
    };
  }, [buyNftData, tokenId, walletAddress]);

  // Stable callbacks
  const toggleList = useCallback((id) => {
    setFilterType(filterOptions[id].key);
    setList((prev) => !prev);
    setSelectedMenu(id);
    setInputOpen(false);
  }, [filterOptions]);

  useEffect(() => {
    setList(true);
  }, [selectedMenu]);

  useEffect(() => {
    setPage(1);
  }, [walletAddress]);

  // Memoized paginated data
  const paginationData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * constant.CARDS_PER_PAGE;
    const lastPageIndex = firstPageIndex + constant.CARDS_PER_PAGE;
    return filteredCards.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, filteredCards]);

  // Dropdown filter handler
  const filterDropdown = useCallback((item, menu) => {
    let fCards = [];
    switch (menu.key) {
      case "price": {
        setPriceHeader(item.text);
        const priceString = item.text.split(" ");
        if (priceString[0] === "<") {
          fCards = nftData.filter(card => card.price < Number(priceString[1]));
        } else if (priceString[0] === ">") {
          fCards = nftData.filter(card => card.price > Number(priceString[1]));
        } else if (priceString[1] === "-") {
          fCards = nftData.filter(card => card.price >= Number(priceString[0]) && card.price <= Number(priceString[2]));
        }
        break;
      }
      case "bgcolor": {
        setPriceHeader("Price");
        setBoxDisplay(true);
        setStyle(item.style);
        const bgcolor = item.style.backgroundColor;
        fCards = nftData.filter(card => card.bgColor === bgcolor);
        break;
      }
      case "pattern": {
        setPriceHeader("Price");
        setBoxDisplay(true);
        setStyle(item.style);
        const pattern = item.name;
        fCards = nftData.filter(card => card.pattern === pattern);
        break;
      }
      default:
        break;
    }
    setStyleType(item.key);
    updateSelectedFilter(menu.id);
    setFilteredCards(fCards);
    setPage(1);
  }, [nftData]);

  // Owner filter handler
  const filterOwner = useCallback((value) => {
    const trimmedValue = value.trim();
    setPriceHeader("Price");
    setBoxDisplay(false);
    setOwner(trimmedValue);
    setFilteredCards(nftData.filter(card => card.address.includes(trimmedValue)));
  }, [nftData]);

  const toggleInput = useCallback((id) => {
    setInputOpen(prev => !prev);
    updateSelectedFilter(id);
    setList(false);
  }, []);

  const resetFilters = useCallback(() => {
    setPriceHeader("Price");
    setList(false);
    setBoxDisplay(false);
    setPage(1);
  }, []);

  const filterViewAll = useCallback((id) => {
    resetFilters();
    updateSelectedFilter(id);
    setFilteredCards(nftData);
  }, [nftData, resetFilters]);

  // Update selected filter button
  const updateSelectedFilter = useCallback((id) => {
    filterOptions.forEach(i => (i.selected = false));
    if (filterOptions[id]) filterOptions[id].selected = true;
  }, [filterOptions]);

  // Sort handler
  const sortFilter = useCallback((item) => {
    // Create a sorted copy of filteredCards
    const fCards = [...filteredCards].sort((a, b) => {
      if (item.sortType === "price") {
        const aPrice = Number(a.price) || 0;
        const bPrice = Number(b.price) || 0;
        return item.direction === "up" ? aPrice - bPrice : bPrice - aPrice;
      } else if (item.sortType === "tnc") {
        const aTnc = a.pieData && a.pieData[2] ? Number(a.pieData[2].value) || 0 : 0;
        const bTnc = b.pieData && b.pieData[2] ? Number(b.pieData[2].value) || 0 : 0;
        return item.direction === "up" ? aTnc - bTnc : bTnc - aTnc;
      }
      return 0;
    });

    // Deselect all sort options and select the current one
    sortOptions.forEach(i => { i.selected = false; });
    if (sortOptions[item.id]) sortOptions[item.id].selected = true;

    setFilteredCards(fCards);
  }, [filteredCards, sortOptions]);

  // Buy NFT handler
  const buyNft = useCallback(async (card) => {
    setSpinner(true);
    await purchaseNFT(card);
    setSpinner(false);
  }, [purchaseNFT]);


  useLayoutEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading]);

  // Render paginated cards
  const renderCardData = useCallback(() => {
    if (paginationData.length === 0 && !loading)
      return <h3 className="sort__text">No item to display</h3>;
    return (
      <>
        {paginationData.map((card) => (
          <div
            className="nft-grid-card__wrapper"
            key={card.id}
            data-id={card.id}
          >
            <Card
              card={card}
              btn={card.forSale && walletAddress !== card.currentOwner ? "Buy" : null}
              download={false}
              overlay={false}
              overlayText={false}
              onBtnOpen={() => buyNft(card)}
              isOwner={walletAddress === card.currentOwner}
            />
          </div>
        ))}
      </>
    );
  }, [paginationData, loading, walletAddress, buyNft]);

  return (
    <section id="buy" className="page-settings">
      <Spinner spinner={spinner || isPurchasing} />
      <h1 className="page-title title-styles">{themeConfig.APP_NFT_PLURAL} for Sale</h1>
      <div className="filter-group">
        <div className="filter">
          <h3 className="filter__text">Filter by</h3>
          <div className="filter-grid">
            {filterOptions.map((item, index) => (
              <div key={index} className="filter-item-grid">
                {item.filterType === "dropdown" ? (
                  <DropDown
                    menu={item}
                    header={item.key === "price" ? priceHeader : item.header}
                    bgStyle={style}
                    styleType={styleType}
                    filterType={filterType}
                    showBox={boxDisplay}
                    showList={isListOpen}
                    onUpdate={filterDropdown}
                    onToggle={toggleList}
                    webPrimaryColor={themeConfig.PRIMARY}
                  />
                ) : item.filterType === "textbox" ? (
                  <>
                    <button
                      className={`filter__btn ${item.selected ? "filter__btn--selected" : ""}`}
                      onClick={() => toggleInput(item.id)}
                    >
                      Owner
                    </button>
                    <div
                      className={`owner-input__wrapper ${isInputOpen ? "d-block" : "d-none"}`}
                    >
                      <input
                        name="owner"
                        className="owner-input"
                        value={owner}
                        onInput={(e) => filterOwner(e.target.value)}
                        placeholder="Type address"
                      />
                    </div>
                  </>
                ) : item.filterType === "button" ? (
                  <button
                    className={`filter__btn ${item.selected ? "filter__btn--selected" : ""}`}
                    onClick={() => filterViewAll(item.id)}
                  >
                    View All
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="filter">
          <h3 className="filter__text">Sort by</h3>
          <div className="filter-grid">
            {sortOptions.map((item) => (
              <div key={item.id} className="filter-item-grid">
                <div
                  className={`sort__btn-wrapper ${item.selected ? "sort__btn--selected" : ""}`}
                  onClick={() => sortFilter(item)}
                >
                  <span className="sort__btn-text">{item.header}</span>
                  <FontAwesomeIcon
                    icon={item.direction === "up" ? faArrowUp : faArrowDown}
                    color={item.selected ? "white" : themeConfig.PRIMARY}
                    size="xl"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        className="nft-cards buy-cards"
        style={{ justifyContent: paginationData.length < 4 ? "center" : undefined }}
      >
        {renderCardData()}
        <Loader loading={loading} />
      </div>
      <Pagination
        currentPage={currentPage}
        totalCount={filteredCards.length}
        siblingCount={3}
        pageSize={constant.CARDS_PER_PAGE}
        onPageChange={setPage}
      />
    </section>
  );
};

export default Buy;
